import React, { useState } from 'react';
import {
  HEADER_COLOR, PURPLE, GREY, money, SortableTh, EyeToggleIcon, DownloadIcon, useTableSort,
} from './weeklyTableHelpers';
import LegacyPerTripTable from './InvoicingSubTables/LegacyPerTripTable';
import FixedPerSeatTable from './InvoicingSubTables/FixedPerSeatTable';
import TieredPerSeatTable from './InvoicingSubTables/TieredPerSeatTable';
import { invoicingSubTableKindFor } from './InvoicingSubTables/invoicingSurface';

// The three shapes an expanded row can draw. invoicingSurface decides WHICH by returning a
// kind; this map is the only place that turns a kind into a component, which is what keeps
// that decision a pure function with no React in it.
const SUB_TABLE_BY_KIND = {
  legacy: LegacyPerTripTable,
  fixed: FixedPerSeatTable,
  tiered: TieredPerSeatTable,
};

/**
 * The expanded sub-table for one company, in the shape its billing model invoices in.
 *
 * Every figure inside is computed server-side; `billing_model` only routes which table is
 * drawn. A tampered payload could therefore change the SHAPE a row renders but not a single
 * number in it.
 *
 * @param {Object} props
 * @param {Object} props.item - The company's invoicing row.
 * @returns {JSX.Element}
 */
const InvoicingSubTable = ({ item }) => {
  const SubTable = SUB_TABLE_BY_KIND[invoicingSubTableKindFor(item)];
  const toggle = item.toggle_table || {};

  return SubTable === LegacyPerTripTable
    ? <SubTable toggleTable={toggle} companyId={item.company_id} />
    : <SubTable perSeat={toggle.per_seat} companyId={item.company_id} />;
};

// What an absent value renders as, matching money() and variancePercent() in
// weeklyTableHelpers so a company with no billing model set reads the same as any other
// missing figure in this table rather than as an empty cell.
const DASH = '\u2014';

// Company, Billing Type, Date Range, Billable Trips, Total Pay, Cancellations, Action. The
// expanded sub-table spans all of them, so adding a column means changing this one number
// rather than hunting for a colSpan.
const COLUMN_COUNT = 7;

// The company's invoice comes in one of three formats depending on its billing model, and
// which one is decided by the SERVER from the Company record — the download request carries
// only the company id and the applied range. This label is a hint for the person clicking,
// and `data-billing-model` on the button is what the acceptance test asserts against; neither
// is ever sent back as the requested format.
//
// @param {Object} item - The company's invoicing row.
// @returns {string} The download button's title and aria-label.
const downloadTitle = (item) => (
  item.billing_model_label
    ? `Download invoicing CSV (${item.billing_model_label})`
    : 'Download invoicing CSV'
);

/**
 * Weekly Overview — Invoicing view table (flag-on). One row per company driven
 * by `toggle_table`. The eye expands an in-place KM-band sub-table; the purple
 * download button hands the row back to the caller for the Xero export.
 *
 * @param {Object} props
 * @param {Array<Object>} props.data - Per-company rows from weekly_summary.
 * @param {(item: Object) => void} props.onDownloadRow - Called with the row item
 *   when its download button is clicked.
 * @returns {JSX.Element}
 */
const InvoicingTable = ({ data, onDownloadRow }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const handleRowClick = (id) => {
    setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  const { sortConfig, handleSort, sortedRows: sortedData } = useTableSort(data);

  return (
    <div className="w-full h-full" style={{ fontFamily: 'Ubuntu, system-ui, sans-serif', fontWeight: 300 }}>
      <div className="overflow-y-auto w-full scrollbar" style={{ maxHeight: '600px' }}>
        <table className="border-collapse w-full" data-testid="weekly-invoicing-table">
          <thead>
            <tr>
              <SortableTh label="Company" sortKey="company_name" sortConfig={sortConfig} onSort={handleSort} testId="invoicing-th-company_name" />
              <SortableTh label="Billing Type" sortKey="billing_model_label" sortConfig={sortConfig} onSort={handleSort} testId="invoicing-th-billing_model" />
              <SortableTh label="Date Range" sortKey="toggle_table.date_range" sortConfig={sortConfig} onSort={handleSort} testId="invoicing-th-date_range" />
              <SortableTh label="Billable Trips" sortKey="toggle_table.billable_trips" sortConfig={sortConfig} onSort={handleSort} testId="invoicing-th-billable_trips" />
              <SortableTh label="Total Pay" sortKey="toggle_table.total_billing" sortConfig={sortConfig} onSort={handleSort} testId="invoicing-th-total_billing" />
              <SortableTh label="Cancellations" sortKey="toggle_table.cancellations" sortConfig={sortConfig} onSort={handleSort} testId="invoicing-th-cancellations" />
              <th className="border-b p-2 text-left" style={{ color: HEADER_COLOR }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item) => {
              const expanded = expandedRows.includes(item.company_id);
              const toggle = item.toggle_table || {};
              return (
                <React.Fragment key={item.company_id}>
                  <tr className="border-t border-b p-2">
                    <td className="border-t border-b p-2 text-gray-700 font-medium text-left">{item.company_name}</td>
                    <td
                      className="border-t text-gray-700 border-b p-2 text-left whitespace-nowrap"
                      data-testid={`invoicing-td-billing_model-${item.company_id}`}
                    >
                      {item.billing_model_label || DASH}
                    </td>
                    <td className="border-t text-gray-700 border-b p-2 text-left">{toggle.date_range}</td>
                    <td className="border-t text-gray-700 border-b p-2 text-left">{toggle.billable_trips}</td>
                    <td className="border-t text-gray-700 border-b p-2 text-left">{money(toggle.total_billing)}</td>
                    <td className="border-t text-gray-700 border-b p-2 text-left">{toggle.cancellations}</td>
                    <td className="border-t border-b p-2 whitespace-nowrap">
                      <div className="flex items-center" style={{ gap: '14px' }}>
                        <button
                          type="button"
                          data-testid={`invoicing-row-expand-${item.company_id}`}
                          data-expanded={expanded}
                          onClick={() => handleRowClick(item.company_id)}
                          className="inline-flex items-center justify-center"
                          style={{
                            background: GREY, width: '41px', height: '41px', borderRadius: '5px',
                          }}
                        >
                          <EyeToggleIcon expanded={expanded} />
                        </button>
                        <button
                          type="button"
                          data-testid={`invoicing-row-download-${item.company_id}`}
                          data-billing-model={item.billing_model || 'unknown'}
                          onClick={() => onDownloadRow(item)}
                          title={downloadTitle(item)}
                          aria-label={downloadTitle(item)}
                          className="inline-flex items-center justify-center"
                          style={{
                            background: PURPLE, width: '41px', height: '41px', borderRadius: '5px',
                          }}
                        >
                          <DownloadIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      {/* Derived, not hardcoded: this drifted once already when the Billing
                          Type column was added, and silently under-spanned the row. */}
                      <td colSpan={COLUMN_COUNT} className="p-0 pr-2">
                        <InvoicingSubTable item={item} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoicingTable;
